import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DivDropdown, FindestRefTextBox, User } from "Components/Shared";
import { PositionedPopup } from "Components/Shared/PositionedPopup";
import { CaseControllerSingleton, UserControllerSingleton } from "Controllers";
import { ToastTypeEnum } from "Enums";
import { ToastHelperSingleton } from "Helpers";
import { useClickOutsideRef, useFetch } from "Hooks";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { CaseContext } from "Stores";
import { TUseFetch, TUserDTO } from "Types";

// Style
import styles from "./information.module.scss";

export const Information: FC = () => {

    // Constants
    const possibleProjectTypes = ["dynamic", "static", "old delivery"];

    // Refs
    const userSearchBoxRef = useRef<HTMLInputElement>(null);
    const userSearchContainerRef = useRef<HTMLDivElement>(null);

    // Contexts
    const { caseState } = useContext(CaseContext);

    // State
    const [users, setUsers] = useState<TUserDTO[]>([]);
    const [foundUsers, setFoundUsers] = useState<TUserDTO[]>([]);
    const [isFoundUserPopupVisible, setIsFoundUserPopupVisible] = useState(false);

    // Load the users associated to the case
    const { fetchedData }: TUseFetch<TUserDTO[]> = useFetch(`api/users/case/${caseState.selectedCaseId}`);

    useEffect(() => {
        if(fetchedData) {
            setUsers(fetchedData);
        }
    }, [fetchedData]);

    const onClickOutsideUserSearch = () => {
        setIsFoundUserPopupVisible(false);
    };

    const onClickInsideUserSearch = () => {
        setIsFoundUserPopupVisible(true);
    };

    useClickOutsideRef(userSearchContainerRef, onClickOutsideUserSearch);

    const onProjectTypeSelected = async (selectedObject: string) => {
        if(!caseState.selectedCase) return;

        const isSuccess = await CaseControllerSingleton.updateDeliveryType(caseState.selectedCase.id, selectedObject);
        if(!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to update the delivery type");
        }
    };

    const onSearchUsernameChanged = async (text: string) => {
        if(!caseState.selectedCase) return;
        
        if(text.trim().length === 0) {
            setFoundUsers([]);
            return;
        }

        const users = await UserControllerSingleton.caseSearchAsync(caseState.selectedCase.id, text);
        setFoundUsers(users);
    };

    const onRemoveUser = async (user: TUserDTO) => {
        if(!caseState.selectedCase) return;

        const isSuccess = await UserControllerSingleton.removeUserFromCaseAsync(caseState.selectedCase.id, user.id);
        if(!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to remove user from the case.");
            return;
        }

        setUsers(users.filter(u => u.id !== user.id));
    };

    const onFoundUserClicked = async (user: TUserDTO) => {
        if(!caseState.selectedCase) return;

        const isSuccess = await UserControllerSingleton.addUserToCaseAsync(caseState.selectedCase.id, user.id);
        if(!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to add user to the case.");
            return;
        }
        setFoundUsers([]);

        const newUsers = [...users, user];
        setUsers(newUsers);
        if(userSearchBoxRef.current) {
            userSearchBoxRef.current.value = "";
        }
    };

    return (
        <div className={styles.informationContainer}>
            <div className={styles.headerContainer}>
                <h2>General project information</h2>
            </div>
            <div className={styles.createdDateContainer}>
                {/* placeholder for created by image */}
                <div className={styles.findExtraUserIcon}> 
                    <FontAwesomeIcon icon={faUserPlus} />
                </div>
                <div className={styles.createdDateText}>
                    Created on {caseState.selectedCase?.startDate}
                </div>
            </div>
            <div className={styles.projectTypeContainer}>
                <div className={styles.projectTypeHeader}>
                    <h3>Type of project</h3>
                </div>
                <div className={styles.projectTypeContent}>
                    <span className={styles.projectTypeContentText}>Delivery page</span>
                    <DivDropdown
                        objectList={possibleProjectTypes}
                        newObjectSelected={onProjectTypeSelected}
                        className={styles.projectTypeDropdown}
                    >
                        {possibleProjectTypes.map((projectType) => {
                            return (
                                <div key={projectType}>
                                    <span className={styles.capitalizeFirstLetter}>{projectType}</span>
                                </div>
                            );
                        })}
                    </DivDropdown>
                </div>
            </div>
            <div className={styles.sharedWithContainer}>
                <div className={styles.sharedWithHeader}>
                    <h3>Project shared with</h3>
                </div>
                <div className={styles.sharedWithList}>
                    <div className={styles.addPersonSection}>
                        <div className={styles.findExtraUserIcon}>
                            <FontAwesomeIcon icon={faUserPlus} />
                        </div>
                        <div ref={userSearchContainerRef} className={styles.addPersonTextboxContainer}>
                            <FindestRefTextBox
                                ref={userSearchBoxRef}
                                onClick={onClickInsideUserSearch}
                                placeholder="Add person"
                                onChange={onSearchUsernameChanged}
                                extraClassName={styles.addPersonTextbox}
                            />
                            {foundUsers.length > 0 && isFoundUserPopupVisible ?
                                <PositionedPopup extraClassName={styles.addPersonPopup}>
                                    <div className={styles.addPersonPopupContent}>
                                        {foundUsers.map((user) => {
                                            return (
                                                <User key={user.id} user={user} className={styles.userHover} onClick={onFoundUserClicked} />
                                            );
                                        })}
                                    </div>
                                </PositionedPopup>
                                : null}
                        </div>
                    </div>
                    <div>
                        {users.map((user) => {
                            return (
                                <User key={user.id} user={user} className={styles.user} roleClassName={styles.role} onRemoveClick={onRemoveUser} />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};